import * as React from 'react';

import { LoaderCircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

const sizeClasses = { sm: 'size-4', md: 'size-6', lg: 'size-10' } as const;

export interface SpinnerProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** `sm` 16px (inline, buttons), `md` 24px (avatars, panels), `lg` 40px (pages, tables). */
  size?: keyof typeof sizeClasses;
}

/**
 * The bare spinning icon, hidden from screen readers. For places that announce loading
 * themselves, like `Button`.
 */
const SpinnerIcon = ({ className }: { className?: string }) => (
  <span
    aria-hidden="true"
    data-slot="spinner"
    className={cn('inline-flex animate-spin', className)}
  >
    <LoaderCircleIcon className="size-full" />
  </span>
);

/**
 * The spinning icon, announced to screen readers as loading. For any loading state outside a
 * button: use it instead of an icon with `animate-spin`. Inside a `Button`, use `isLoading`.
 */
const Spinner = ({
  className,
  size = 'sm',
  ref,
  ...props
}: SpinnerProps & { ref?: React.Ref<HTMLSpanElement> }) => {
  const { t } = useTranslation();
  return (
    <span
      ref={ref}
      role="status"
      aria-live="polite"
      aria-label={t('common.loading')}
      className={cn('inline-flex items-center justify-center text-foreground', className)}
      {...props}
    >
      <SpinnerIcon className={sizeClasses[size]} />
    </span>
  );
};

export { SpinnerIcon, Spinner };
